import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography,
  Divider, Grid, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Card, CardHeader, CardContent, Tabs, Tab
} from '@material-ui/core';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Save as SaveIcon,
  Cancel as CancelIcon, Money as MoneyIcon, List as ListIcon, TableChart as TableIcon
} from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { useRequest, useNotif } from 'src/hooks';
import { DataTable } from 'src/components';
import numeral from 'numeral';

const SalaryMatrix = ({ kvsKey, config }) => {
  const notify = useNotif();
  const request = useRequest();
  const { kvs = {} } = useSelector(state => state.bizMeta);

  // Get the specific KVS config for this component
  const kvsData = kvs[kvsKey];
  const [salaryGradeMap, setSalaryGradeMap] = useState([]);
  const [salaryDialogOpen, setSalaryDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [salaryViewMode, setSalaryViewMode] = useState(0); // 0 = list, 1 = matrix
  const [salaryForm, setSalaryForm] = useState({
    id1: '', // Salary Grade
    id2: '', // Salary Step
    value: '' // Salary Amount
  });

  // Load data from same endpoint as UserChoices
  useEffect(() => {
    loadSalaryMatrixData();
  }, [kvsKey]);

  const loadSalaryMatrixData = async () => {
    try {
      const response = await request.post('/setup/biz/kvs/items', { keys: [kvsKey] });
      if (response.success) {
        // The response.data is an array of objects with key, value, id1, id2
        const rawData = response.data || [];
        
        // Filter data for this specific kvsKey and transform
        const filteredData = rawData.filter(item => item.key === kvsKey);
        
        const transformedData = filteredData.map(item => ({
          id1: item.id1,
          id2: item.id2,
          value: item.value
        }));
        setSalaryGradeMap(transformedData);
      }
    } catch (error) {
      setSalaryGradeMap([]);
    }
  };
  const handleSalaryAdd = () => {
    setDialogMode('add');
    setEditingItem(null);
    setSalaryForm({ id1: '', id2: '', value: '' });
    setSalaryDialogOpen(true);
  };

  const handleSalaryEdit = (item) => {
    setDialogMode('edit');
    setEditingItem(item);
    setSalaryForm({
      id1: item.id1,
      id2: item.id2,
      value: item.value
    });
    setSalaryDialogOpen(true);
  };

  const handleSalaryDelete = async (item) => {
    try {
      await request.post('/setup/biz/kvs/delete', {
        [kvsKey]: { 
          id1: item.id1,
          id2: item.id2,
        }
      });
      const newSalaryMap = salaryGradeMap.filter(
        entry => !(entry.id1 === item.id1 && entry.id2 === item.id2)
      );
      setSalaryGradeMap(newSalaryMap);
      
      notify.success('Entry deleted successfully');
    } catch (error) {
      notify.error('Failed to delete entry');
    }
  };

  const handleSalarySave = async () => {
    if (!salaryForm.id1 || !salaryForm.id2 || !salaryForm.value) {
      notify.error('Please fill in all fields');
      return;
    }

    let newSalaryMap;
    if (dialogMode === 'add') {
      const exists = salaryGradeMap.some(
        entry => entry.id1 === parseInt(salaryForm.id1) && entry.id2 === parseInt(salaryForm.id2)
      );
      
      if (exists) {
        notify.error('Entry already exists for this grade and step');
        return;
      }
      
      newSalaryMap = [...salaryGradeMap, {
        id1: parseInt(salaryForm.id1),
        id2: parseInt(salaryForm.id2),
        value: parseFloat(salaryForm.value)
      }];
    } else {
      newSalaryMap = salaryGradeMap.map(entry => 
        entry.id1 === editingItem.id1 && entry.id2 === editingItem.id2
          ? {
              id1: parseInt(salaryForm.id1),
              id2: parseInt(salaryForm.id2),
              value: parseFloat(salaryForm.value)
            }
          : entry
      );
    }

    try {
      await saveSalaryGradeMap(newSalaryMap);
      setSalaryDialogOpen(false);
      notify.success(`Entry ${dialogMode === 'add' ? 'added' : 'updated'} successfully`);
    } catch (error) {
      notify.error(`Failed to ${dialogMode} entry`);
    }
  };

  const saveSalaryGradeMap = async (newMap) => {
    try {
      const payload = {
        [kvsKey]: {
          id1: parseInt(salaryForm.id1),
          id2: parseInt(salaryForm.id2),
          value: parseFloat(salaryForm.value)
        }
      };

      const response = await request.post('/setup/biz/kvs/save', payload);
      if (response.success) {
        await loadSalaryMatrixData();
      } else {
        throw new Error(response.errorMessage || 'Failed to save');
      }
    } catch (error) {
      console.error('Save error:', error);
      throw error;
    }
  };
  const generateSalaryMatrix = () => {
    const grades = [...new Set(salaryGradeMap.map(item => item.id1))].sort((a, b) => a - b);
    const steps = [...new Set(salaryGradeMap.map(item => item.id2))].sort((a, b) => a - b);
    return { grades, steps };
  };

  const getSalaryValue = (grade, step) => {
    const entry = salaryGradeMap.find(item => item.id1 === grade && item.id2 === step);
    return entry ? entry.value : null;
  };

  const getSalaryEntry = (grade, step) => {
    return salaryGradeMap.find(item => item.id1 === grade && item.id2 === step);
  };

  const renderSalaryMatrixView = () => {
    const { grades, steps } = generateSalaryMatrix();
    
    if (grades.length === 0 || steps.length === 0) {
    }

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: 'bold' }}>Grade</TableCell>
              {steps.map(step => (
                <TableCell key={step} align="center" style={{ fontWeight: 'bold' }}>
                  Step {step}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {grades.map(grade => (
              <TableRow key={grade}>
                <TableCell style={{ fontWeight: 'bold' }}>
                  Grade {grade}
                </TableCell>
                {steps.map(step => {
                  const entry = getSalaryEntry(grade, step);
                  return (
                    <TableCell key={step} align="right">
                      {entry ? (
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Typography variant="body2">
                            {numeral(entry.value).format('0,0')}
                          </Typography>
                          <Box>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleSalaryEdit(entry)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  const sortedSalaryGradeMap = [...salaryGradeMap]
    .map((item, index) => ({ ...item, id: `${item.id1}-${item.id2}` }))
    .sort((a, b) => a.id1 - b.id1 || a.id2 - b.id2);

  return (
    <Box>
      <Typography variant="h5" gutterBottom style={{ marginBottom: 24 }}>
        <MoneyIcon style={{ marginRight: 8, verticalAlign: 'middle' }} />
        {kvsData?.caption || 'Salary Matrix'}
      </Typography>

      {/* Salary Grade Matrix */}
      <Card style={{ marginBottom: 24 }}>
        <CardHeader
          title={kvsData?.caption || 'Salary Matrix'}
          titleTypographyProps={{ variant: 'h6' }}
          action={
            <Box display="flex" alignItems="center">
              {kvsKey !== 'hazard_pay_table' && (
                <Tabs
                  value={salaryViewMode}
                  onChange={(e, newValue) => setSalaryViewMode(newValue)}
                  indicatorColor="primary"
                  textColor="primary"
                  size="small"
                >
                  <Tab icon={<ListIcon />} label="List" />
                  <Tab icon={<TableIcon />} label="Matrix" />
                </Tabs>
              )}
              <Button
                color="primary"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleSalaryAdd}
                size="small"
                style={{ marginLeft: 16 }}
              >
                Add Entry
              </Button>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          {salaryGradeMap.length === 0 ? (
            <Box p={2}>
            </Box>
          ) : (salaryViewMode === 0 || kvsKey === 'hazard_pay_table') ? (
            // List View using DataTable
            <DataTable
              columns={[
                {
                  field: 'id1',
                  headerName: kvsData?.id1?.caption || 'Grade',
                  width: 120,
                  align: 'center'
                },
                {
                  field: 'id2',
                  headerName: kvsData?.id2?.caption || 'Step',
                  width: 120,
                  align: 'center'
                },
                {
                  field: 'value',
                  headerName: 'Value',
                  width: 150,
                  align: 'right',
                  renderCell: ({ value }) => (
                    kvsKey === 'hazard_pay_table' 
                      ? numeral(value).format('0.00%') 
                      : numeral(value).format('0,0')
                  )
                },
                {
                  field: 'actions',
                  headerName: 'Actions',
                  width: 120,
                  align: 'center',
                  renderCell: ({ row }) => (
                    <Box display="flex" gap={1}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleSalaryEdit(row)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleSalaryDelete(row)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )
                }
              ]}
              data={sortedSalaryGradeMap}
              rowIDKey="id"
              hideToolbar
              paginate={false}
              showTableBorder
              showCellRightBorder
            />
          ) : (
            renderSalaryMatrixView()
          )}
        </CardContent>
      </Card>

      {/* Salary Dialog */}
      <Dialog open={salaryDialogOpen} onClose={() => setSalaryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? `Add ${kvsData?.caption || 'Entry'}` : `Edit ${kvsData?.caption || 'Entry'}`}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={kvsData?.id1?.caption || 'Grade'}
                type="number"
                value={salaryForm.id1}
                onChange={(e) => setSalaryForm({ ...salaryForm, id1: e.target.value })}
                inputProps={{ min: 1, max: 33 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={kvsData?.id2?.caption || 'Step'}
                type="number"
                value={salaryForm.id2}
                onChange={(e) => setSalaryForm({ ...salaryForm, id2: e.target.value })}
                inputProps={{ min: 1, max: 8 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Value"
                type="number"
                value={salaryForm.value}
                onChange={(e) => setSalaryForm({ ...salaryForm, value: e.target.value })}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSalaryDialogOpen(false)}>
            <CancelIcon style={{ marginRight: 4 }} />
            Cancel
          </Button>
          <Button onClick={handleSalarySave} color="primary" variant="contained">
            <SaveIcon style={{ marginRight: 4 }} />
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalaryMatrix;
